import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FlaskConical, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateEvalRunModal } from './CreateEvalRunModal';
import { useEvalRunColumns } from './columns';

export const Route = createFileRoute('/admin-console/ai/eval-runs')({
    staticData: {
        permissions: ['admin_ai:read'],
        presentation: 'fullPage' as const,
        breadcrumb: {
            title: 'Eval runs',
        },
        ui: {
            icon: FlaskConical,
            title: 'Eval runs',
            subtitle: 'View and compare AI prompt evaluation results',
            widthVariant: 'fullWidth',
            menuDescription: 'AI prompt evaluation history',
        },
    },
    component: EvalRuns,
});

type StatusFilter = 'all' | 'RUNNING' | 'COMPLETED' | 'FAILED';

function EvalRuns() {
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [limit] = useState(20);
    const [offset, setOffset] = useState(0);

    const queryInput = {
        status: statusFilter === 'all' ? undefined : (statusFilter as 'RUNNING' | 'COMPLETED' | 'FAILED'),
        limit,
        offset,
    };

    const { data, isLoading, isFetching, error, refetch } = trpc.aiEval.listEvalRuns.useQuery(queryInput);

    const { mutate: deleteEvalRun } = trpc.aiEval.deleteEvalRun.useMutation({
        onSuccess: () => {
            message.success('Eval run deleted');
            void trpcUtils.aiEval.listEvalRuns.invalidate();
        },
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: setBaseline } = trpc.aiEval.setBaseline.useMutation({
        onSuccess: () => {
            void trpcUtils.aiEval.listEvalRuns.invalidate();
        },
        onError: (err) => errorMessage.show(err),
    });

    const columns = useEvalRunColumns({
        onView: (id) => {
            void navigate({
                to: '/admin-console/ai/eval-runs/$evalRunId',
                params: { evalRunId: String(id) },
            });
        },
        onCompare: (id) => {
            void navigate({
                to: '/admin-console/ai/eval-runs/compare',
                search: { runA: String(id) },
            });
        },
        onToggleBaseline: (id, isBaselineVal) => {
            setBaseline({ id, isBaseline: isBaselineVal });
        },
        onDelete: async (id) => {
            return new Promise<void>((resolve, reject) => {
                deleteEvalRun({ ids: [id] }, { onSuccess: () => resolve(), onError: reject });
            });
        },
        isReadOnly,
    });

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams: { limit, offset, orderBy: {} },
        onParamsChange: (params) => {
            if (params.offset !== undefined) setOffset(params.offset);
        },
    });
    const hasBlockingError = hasBlockingQueryError(error, data);

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="ai" />
            <PageLayoutHeader
                rightBottomAddon={
                    <Space>
                        <SegmentedControl<StatusFilter>
                            value={statusFilter}
                            onChange={(val) => {
                                setStatusFilter(val);
                                setOffset(0);
                            }}
                            size="small"
                            options={[
                                { label: 'All', value: 'all' },
                                { label: 'Running', value: 'RUNNING' },
                                { label: 'Completed', value: 'COMPLETED' },
                                { label: 'Failed', value: 'FAILED' },
                            ]}
                            aria-label="Filter eval runs by status"
                            variant="bordered"
                        />
                        {!isReadOnly && (
                            <Button type="primary" icon={<Plus />} onClick={() => setIsCreateModalOpen(true)}>
                                New eval run
                            </Button>
                        )}
                    </Space>
                }
            />

            {hasBlockingError ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't load eval runs"
                />
            ) : (
                <>
                    {error && (
                        <InlineQueryError
                            error={error}
                            onRetry={() => void refetch()}
                            retrying={isFetching}
                            title="Couldn't refresh eval runs"
                            compact
                        />
                    )}
                    <PrismaTable {...tableProps} size="small" scroll={{ x: 'max-content' }} />
                </>
            )}

            <CreateEvalRunModal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={(runId) => {
                    setIsCreateModalOpen(false);
                    void trpcUtils.aiEval.listEvalRuns.invalidate();
                    void navigate({
                        to: '/admin-console/ai/eval-runs/$evalRunId',
                        params: { evalRunId: String(runId) },
                    });
                }}
            />
            <Outlet />
        </PageController>
    );
}
